import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '../utils/API';
import './awards.css';

export default function NominationDecline() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState('loading');
  const [info, setInfo] = useState(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    API.getNominationInfo(token)
      .then(res => {
        setInfo(res.data);
        if (res.data.status === 'accepted') {
          setState('already_accepted');
        } else if (res.data.status === 'declined') {
          setState('already_declined');
        } else {
          setState('confirm');
        }
      })
      .catch(err => {
        setError(err.response?.data?.msg || 'Something went wrong. Please try again.');
        setState('error');
      });
  }, [token]);

  const handleAccept = async () => {
    setWorking(true);
    try {
      const res = await API.acceptNomination(token);
      setInfo(prev => ({ ...prev, ...res.data }));
      setState('accepted');
    } catch (err) {
      setError(err.response?.data?.msg || 'Could not accept nomination. Please try again.');
    } finally {
      setWorking(false);
    }
  };

  const handleDecline = async () => {
    setWorking(true);
    try {
      await API.declineNomination(token);
      setState('declined');
    } catch (err) {
      setError(err.response?.data?.msg || 'Could not decline nomination. Please try again.');
    } finally {
      setWorking(false);
    }
  };

  if (state === 'loading') {
    return (
      <div className="awards-page">
        <div className="awards-loading">Loading…</div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="awards-page">
        <div className="nom-response-card nom-response-error">
          <i className="bi bi-exclamation-circle nom-response-icon" />
          <h2 className="nom-response-title">Something went wrong</h2>
          <p className="nom-response-body">{error}</p>
        </div>
      </div>
    );
  }

  if (state === 'accepted') {
    const firstName = info?.nominee_name?.split(' ')[0] || 'there';
    const categoryLabel = info?.category_label || 'this award';
    return (
      <div className="awards-page">
        <div className="nom-response-card nom-response-accept">
          <i className="bi bi-trophy-fill nom-response-icon" />
          <h2 className="nom-response-title">Congratulations, {firstName}!</h2>
          <p className="nom-response-body">
            Your nomination for {categoryLabel} has been accepted. You're officially on the ballot — your peers will cast their votes soon. Well deserved!
          </p>
          <div className="nom-response-divider" />
          <p className="nom-response-cta-text">
            Pay it forward — is there someone in your network who deserves recognition from their peers?
          </p>
          <button className="nom-response-btn" onClick={() => navigate('/awards')}>
            <i className="bi bi-pencil-square me-2" />Nominate Someone
          </button>
        </div>
      </div>
    );
  }

  if (state === 'already_accepted') {
    return (
      <div className="awards-page">
        <div className="nom-response-card nom-response-accept">
          <i className="bi bi-trophy-fill nom-response-icon" />
          <h2 className="nom-response-title">You're already on the ballot!</h2>
          <p className="nom-response-body">
            Your nomination for {info?.category_label || 'this award'} has already been accepted. Good luck!
          </p>
          <div className="nom-response-divider" />
          <p className="nom-response-cta-text">
            Know someone else who deserves recognition?
          </p>
          <button className="nom-response-btn" onClick={() => navigate('/awards')}>
            <i className="bi bi-pencil-square me-2" />Nominate Someone
          </button>
        </div>
      </div>
    );
  }

  if (state === 'declined' || state === 'already_declined') {
    return (
      <div className="awards-page">
        <div className="nom-response-card nom-response-neutral">
          <i className="bi bi-heart nom-response-icon" />
          <h2 className="nom-response-title">We completely understand.</h2>
          <p className="nom-response-body">
            {state === 'already_declined'
              ? `Your nomination for ${info?.category_label || 'this award'} has already been declined.`
              : `Your nomination for ${info?.category_label || 'this award'} has been declined.`}
            {' '}It's still meaningful that a peer thought of you — your work clearly makes an impression on the people around you.
          </p>
          <div className="nom-response-divider" />
          <p className="nom-response-cta-text">
            Is there someone in your network who you believe deserves recognition from their peers?
          </p>
          <button className="nom-response-btn" onClick={() => navigate('/awards')}>
            <i className="bi bi-pencil-square me-2" />Nominate Someone
          </button>
        </div>
      </div>
    );
  }

  // state === 'confirm'
  const firstName = info?.nominee_name?.split(' ')[0] || 'there';
  const categoryLabel = info?.category_label || 'this award';

  return (
    <div className="awards-page">
      <div className="nom-response-card nom-response-caution">
        <i className="bi bi-award nom-response-icon" />
        <h2 className="nom-response-title">Wait — a peer believed in you.</h2>
        <p className="nom-response-body">
          {firstName}, someone who knows your work nominated you for <strong>{categoryLabel}</strong> because
          they believe you deserve recognition from your peers in California's legislative and advocacy community.
        </p>
        <p className="nom-response-body">
          You don't have to accept, but know that this nomination reflects the genuine respect a colleague has for what you do.
        </p>
        {error && (
          <div className="nom-error" style={{ marginBottom: '0.5rem' }}>
            <i className="bi bi-exclamation-circle me-1" />{error}
          </div>
        )}
        <div className="nom-response-actions">
          <button
            className="nom-response-btn"
            onClick={handleAccept}
            disabled={working}
          >
            <i className="bi bi-trophy me-2" />{working ? 'Processing…' : 'Accept — I\'m honored'}
          </button>
          <button
            className="nom-response-btn-ghost"
            onClick={handleDecline}
            disabled={working}
          >
            {working ? 'Processing…' : 'No thank you, decline'}
          </button>
        </div>
      </div>
    </div>
  );
}
